/**
 * FilterBar: the toolbar above every list (D-2605). Fields are labelled selects / inputs; chips are toggles with
 * `aria-pressed`; the end slot holds counts and secondary actions. Collapses to one field per row on phones.
 */
import { X } from 'lucide-react';
import type { ReactNode } from 'react';

export function FilterBar({
  children,
  end,
  label = 'Filters',
}: {
  children: ReactNode;
  end?: ReactNode;
  label?: string;
}) {
  return (
    <div className="wdc-filterbar" role="group" aria-label={label}>
      {children}
      {end ? <div className="wdc-filterbar__end">{end}</div> : null}
    </div>
  );
}

export function FilterField({
  label,
  htmlFor,
  grow,
  children,
}: {
  label: ReactNode;
  htmlFor?: string;
  grow?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="wdc-filterbar__field" data-grow={grow ? 'true' : undefined}>
      <label className="wdc-filterbar__label" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
    </div>
  );
}

/** Native select styled for the filter bar. */
export function FilterSelect({
  id,
  value,
  onChange,
  options,
  'aria-label': ariaLabel,
  'data-testid': testId,
}: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  options: ReadonlyArray<{ value: string; label: string }>;
  'aria-label'?: string;
  'data-testid'?: string;
}) {
  return (
    <select
      id={id}
      className="wd-select"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={ariaLabel}
      data-testid={testId}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export function Chip({
  pressed,
  onClick,
  children,
  count,
  'data-testid': testId,
}: {
  pressed?: boolean;
  onClick: () => void;
  children: ReactNode;
  count?: number | null;
  'data-testid'?: string;
}) {
  return (
    <button type="button" className="wdc-chip" aria-pressed={pressed} onClick={onClick} data-testid={testId}>
      {children}
      {typeof count === 'number' ? <span className="wdc-chip__count wd-tabular">{count}</span> : null}
    </button>
  );
}

export function Chips({ children, label }: { children: ReactNode; label: string }) {
  return (
    <div className="wdc-chips" role="group" aria-label={label}>
      {children}
    </div>
  );
}

/** "Clear filters" link-button shown when any filter is active. */
export function ClearFilters({ onClick, active }: { onClick: () => void; active: boolean }) {
  if (!active) return null;
  return (
    <button type="button" className="wd-btn" data-variant="quiet" onClick={onClick}>
      <X size={14} aria-hidden="true" /> Clear filters
    </button>
  );
}
